import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Download, SlidersHorizontal, ChevronRight as ChevronRightIcon } from 'lucide-react'
import {
  Card,
  CardContent,
  AlertMessage,
  Toast,
  ToastType,
  ListPageSkeleton,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui'
import { LeadFilters } from '../../components/filters'
import { Lead, LeadFiltersAPI, Vehicle } from '../../types'
import { useLeads } from '../../hooks/useLeads'
import { useLeadOptions } from '../../hooks/useLeadOptions'
import { useUsers } from '../../hooks/useUsers'
import { useVehicles } from '../../hooks/useVehicles'
import { useSalesFunnels } from '../../hooks/useSalesFunnels'
import { apiService } from '../../services/api'
import { cn } from '../../utils/cn'

type TriStateFilter = 'all' | 'true' | 'false'

function formatLeadPrimaryPhone(lead: Lead): string {
  const list = lead.phones ?? []
  const p = list.find((x) => x.isPrimary) ?? list[0]
  if (p) return `${p.ddi}${p.ddd}${p.phone}`
  return lead.phone ?? ''
}

function formatLeadPrimaryEmail(lead: Lead): string {
  const list = lead.emails ?? []
  const e = list.find((x) => x.isPrimary) ?? list[0]
  if (e) return e.email
  return lead.email ?? ''
}

export const LeadsReportPage: React.FC = () => {
  const { t } = useTranslation()
  const { leads, isLoading, error, pagination, fetchLeads } = useLeads()
  const { options: leadOptions } = useLeadOptions()
  const { users, isLoading: isUsersLoading } = useUsers()
  const { isLoading: isVehiclesLoading } = useVehicles()
  const { defaultFunnel } = useSalesFunnels()

  const [searchTerm, setSearchTerm] = useState('')
  const [nameFilter, setNameFilter] = useState('')
  const [phoneFilter, setPhoneFilter] = useState('')
  const [emailFilter, setEmailFilter] = useState('')
  const [documentFilter, setDocumentFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [sourceFilter, setSourceFilter] = useState<string>('all')
  const [assignedToFilter, setAssignedToFilter] = useState('')
  const [assignedToFilterLabel, setAssignedToFilterLabel] = useState('')
  const [vehicleInterestFilter, setVehicleInterestFilter] = useState('')
  const [funnelStageFilter, setFunnelStageFilter] = useState<string>('all')
  const [createdAtFromFilter, setCreatedAtFromFilter] = useState(() => {
    const now = new Date()
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
    return firstDay.toISOString().split('T')[0]
  })
  const [createdAtToFilter, setCreatedAtToFilter] = useState(() => {
    const today = new Date()
    return today.toISOString().split('T')[0]
  })
  const [hasProposalFilter, setHasProposalFilter] = useState<TriStateFilter>('all')
  const [isConvertedFilter, setIsConvertedFilter] = useState<TriStateFilter>('all')
  const [filtersPanelOpen, setFiltersPanelOpen] = useState(true)
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null)
  const [exportLoading, setExportLoading] = useState(false)
  const [vehicleSearchTerm, setVehicleSearchTerm] = useState('')
  const [vehicleSearchResults, setVehicleSearchResults] = useState<Vehicle[]>([])

  const userComboboxOptions = useMemo(
    () =>
      users
        .filter((user) => (user.isActive !== undefined ? user.isActive : user.status === 'active'))
        .map((user) => ({
          id: user.id,
          label: user.name,
          meta: user.email,
        })),
    [users],
  )

  const vehicleComboboxOptions = useMemo(() => {
    return vehicleSearchResults.map((v) => {
      const brandName =
        typeof v.brand === 'string' ? v.brand : v.brand?.name ?? v.catalog?.brand?.name ?? ''
      const modelName =
        typeof v.model === 'string' ? v.model : v.model?.name ?? v.catalog?.model?.name ?? ''
      const versionName =
        typeof v.version === 'string' ? v.version : v.version?.name ?? v.catalog?.version?.name ?? ''
      return {
        id: v.id,
        label: `${brandName} ${modelName} ${versionName}`.trim(),
        meta: v.identification?.plate ?? v.identification?.chassis ?? '',
      }
    })
  }, [vehicleSearchResults])

  const stageNameById = useMemo(() => {
    const map = new Map<string, string>()
    const stages = defaultFunnel?.stages ?? []
    stages.forEach((s) => {
      if (s.id) map.set(s.id, s.name ?? s.id)
    })
    return map
  }, [defaultFunnel?.stages])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (vehicleSearchTerm.trim().length >= 3) {
        apiService
          .getVehicles({ name: vehicleSearchTerm, limit: 20, status: 'in_stock' })
          .then((res) => {
            if (res && typeof res === 'object' && 'vehicles' in res) {
              setVehicleSearchResults((res as { vehicles: Vehicle[] }).vehicles || [])
            } else if (Array.isArray(res)) {
              setVehicleSearchResults(res as Vehicle[])
            } else {
              setVehicleSearchResults([])
            }
          })
          .catch(() => setVehicleSearchResults([]))
      } else {
        setVehicleSearchResults([])
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [vehicleSearchTerm])

  const buildFilterParams = useCallback((): LeadFiltersAPI => {
    const params: LeadFiltersAPI = { page: 1, limit: 500 }
    const safeSearch = searchTerm.trim()
    const safeName = nameFilter.trim()
    const safePhone = phoneFilter.trim()
    const safeEmail = emailFilter.trim()
    const safeDocument = documentFilter.trim()
    const safeAssignedTo = assignedToFilter.trim()
    const safeVehicleInterest = vehicleInterestFilter.trim()

    if (statusFilter !== 'all') params.status = statusFilter
    if (sourceFilter !== 'all') params.source = sourceFilter
    if (funnelStageFilter !== 'all') params.funnelStage = funnelStageFilter
    if (hasProposalFilter !== 'all') params.hasProposal = hasProposalFilter === 'true'
    if (isConvertedFilter !== 'all') params.isConverted = isConvertedFilter === 'true'
    if (safeSearch) params.search = safeSearch
    if (safeName) params.name = safeName
    if (safePhone) params.phone = safePhone
    if (safeEmail) params.email = safeEmail
    if (safeDocument) params.document = safeDocument
    if (safeAssignedTo) params.assignedTo = safeAssignedTo
    if (safeVehicleInterest) params.vehicleInterestId = safeVehicleInterest
    if (createdAtFromFilter) params.createdAtFrom = createdAtFromFilter
    if (createdAtToFilter) params.createdAtTo = createdAtToFilter

    return params
  }, [
    searchTerm,
    nameFilter,
    phoneFilter,
    emailFilter,
    documentFilter,
    statusFilter,
    sourceFilter,
    funnelStageFilter,
    hasProposalFilter,
    isConvertedFilter,
    assignedToFilter,
    vehicleInterestFilter,
    createdAtFromFilter,
    createdAtToFilter,
  ])

  const buildExportParams = useCallback((): LeadFiltersAPI => {
    const p = buildFilterParams()
    const { page: _p, limit: _l, ...rest } = p
    return rest
  }, [buildFilterParams])

  useEffect(() => {
    const params = buildFilterParams()
    const hasTextFilter =
      !!searchTerm.trim() ||
      !!nameFilter.trim() ||
      !!phoneFilter.trim() ||
      !!emailFilter.trim() ||
      !!documentFilter.trim() ||
      !!vehicleInterestFilter.trim()

    const timer = setTimeout(() => fetchLeads(params), hasTextFilter ? 500 : 0)
    return () => clearTimeout(timer)
  }, [buildFilterParams, fetchLeads, searchTerm, nameFilter, phoneFilter, emailFilter, documentFilter, vehicleInterestFilter])

  const hasActiveFilters =
    statusFilter !== 'all' ||
    sourceFilter !== 'all' ||
    funnelStageFilter !== 'all' ||
    hasProposalFilter !== 'all' ||
    isConvertedFilter !== 'all' ||
    !!searchTerm.trim() ||
    !!nameFilter.trim() ||
    !!phoneFilter.trim() ||
    !!emailFilter.trim() ||
    !!documentFilter.trim() ||
    !!assignedToFilter ||
    !!vehicleInterestFilter.trim() ||
    !!createdAtFromFilter ||
    !!createdAtToFilter

  const clearAllFilters = () => {
    setSearchTerm('')
    setNameFilter('')
    setPhoneFilter('')
    setEmailFilter('')
    setDocumentFilter('')
    setStatusFilter('all')
    setSourceFilter('all')
    setAssignedToFilter('')
    setAssignedToFilterLabel('')
    setVehicleInterestFilter('')
    setVehicleSearchTerm('')
    setVehicleSearchResults([])
    setFunnelStageFilter('all')
    const now = new Date()
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
    const today = new Date()
    setCreatedAtFromFilter(firstDay.toISOString().split('T')[0])
    setCreatedAtToFilter(today.toISOString().split('T')[0])
    setHasProposalFilter('all')
    setIsConvertedFilter('all')
  }

  const handleExportCsv = async () => {
    setExportLoading(true)
    try {
      const blob = await apiService.downloadLeadsReportCsv(buildExportParams())
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `leads-report-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      setToast({ message: t('leads.report.exportSuccess'), type: 'success' })
    } catch {
      setToast({ message: t('leads.report.exportError'), type: 'error' })
    } finally {
      setExportLoading(false)
    }
  }

  const formatPhoneMask = (value: string) => {
    const digits = value.replace(/\D/g, '')
    if (digits.length <= 11) {
      let masked = ''
      if (digits.length > 0) masked = `(${digits.slice(0, 2)}`
      if (digits.length >= 3) masked += `) ${digits.slice(2, 7)}`
      if (digits.length >= 7) masked += `-${digits.slice(7, 11)}`
      return masked
    }
    return value
  }

  const handlePhoneChange = (value: string) => {
    setPhoneFilter(formatPhoneMask(value))
  }

  return (
    <div className="space-y-5">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1
            className="text-xl font-bold text-foreground sm:text-2xl"
            style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}
          >
            {t('leads.report.title')}
          </h1>
          <p className="mt-0.5 text-sm text-muted-foreground">{t('leads.report.subtitle')}</p>
        </div>
        <Button
          type="button"
          onClick={handleExportCsv}
          disabled={exportLoading}
          className="inline-flex items-center gap-2 shrink-0"
        >
          <Download className="h-4 w-4" />
          {exportLoading ? t('leads.report.exporting') : t('leads.report.exportCsv')}
        </Button>
      </div>

      {error && <AlertMessage variant="error" message={error} />}

      <div className="flex gap-4 items-start">
        <div
          className={cn(
            'flex-shrink-0 transition-all duration-200',
            filtersPanelOpen ? 'w-56' : 'w-10',
            'hidden lg:block',
          )}
        >
          {filtersPanelOpen ? (
            <LeadFilters
              nameFilter={nameFilter}
              phoneFilter={phoneFilter}
              emailFilter={emailFilter}
              documentFilter={documentFilter}
              statusFilter={statusFilter}
              sourceFilter={sourceFilter}
              funnelStageFilter={funnelStageFilter}
              assignedTo={assignedToFilter}
              assignedToLabel={assignedToFilterLabel}
              vehicleInterest={vehicleInterestFilter}
              vehicleSearchTerm={vehicleSearchTerm}
              createdAtFrom={createdAtFromFilter}
              createdAtTo={createdAtToFilter}
              hasProposalFilter={hasProposalFilter}
              isConvertedFilter={isConvertedFilter}
              leadOptions={leadOptions}
              userOptions={userComboboxOptions}
              isUsersLoading={isUsersLoading}
              vehicleOptions={vehicleComboboxOptions}
              isVehiclesLoading={isVehiclesLoading}
              onNameChange={setNameFilter}
              onPhoneChange={handlePhoneChange}
              onEmailChange={setEmailFilter}
              onDocumentChange={setDocumentFilter}
              onStatusChange={setStatusFilter}
              onSourceChange={setSourceFilter}
              onFunnelStageChange={setFunnelStageFilter}
              onAssignedToChange={(opt) => {
                setAssignedToFilter(opt.id)
                setAssignedToFilterLabel(opt.label)
              }}
              onAssignedToLabelChange={setAssignedToFilterLabel}
              onVehicleInterestChange={(opt) => setVehicleInterestFilter(opt.id)}
              onVehicleSearchTermChange={setVehicleSearchTerm}
              onCreatedAtFromChange={setCreatedAtFromFilter}
              onCreatedAtToChange={setCreatedAtToFilter}
              onHasProposalChange={(value: string) => setHasProposalFilter(value as TriStateFilter)}
              onIsConvertedChange={(value: string) => setIsConvertedFilter(value as TriStateFilter)}
              onClearFilters={clearAllFilters}
              activeCount={
                hasActiveFilters
                  ? [
                      statusFilter,
                      sourceFilter,
                      funnelStageFilter,
                      assignedToFilter,
                      vehicleInterestFilter,
                      createdAtFromFilter,
                      createdAtToFilter,
                      hasProposalFilter,
                      isConvertedFilter,
                    ].filter((v) => v && v !== 'all').length
                  : 0
              }
            />
          ) : (
            <button
              type="button"
              onClick={() => setFiltersPanelOpen(true)}
              className={cn(
                'w-10 flex flex-col items-center gap-2 py-3 rounded-lg border border-border bg-card hover:bg-muted transition-colors',
                hasActiveFilters && 'border-accent-300 dark:border-accent-700',
              )}
            >
              <SlidersHorizontal
                className={cn('h-4 w-4', hasActiveFilters ? 'text-accent-600' : 'text-muted-foreground')}
              />
              <ChevronRightIcon className="h-3 w-3 text-muted-foreground" />
              {hasActiveFilters && <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />}
            </button>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <Card>
            <CardContent className="p-0">
              <div className="p-4 border-b border-border bg-muted/30">
                <div className="flex flex-wrap gap-3 items-end">
                  <div className="space-y-1.5 min-w-[140px]">
                    <label className="text-xs font-medium text-muted-foreground">
                      {t('leads.report.filters.dateFrom')}
                    </label>
                    <Input
                      type="date"
                      value={createdAtFromFilter}
                      onChange={(e) => setCreatedAtFromFilter(e.target.value)}
                      className="h-9 text-sm"
                    />
                  </div>
                  
                  <div className="space-y-1.5 min-w-[140px]">
                    <label className="text-xs font-medium text-muted-foreground">
                      {t('leads.report.filters.dateTo')}
                    </label>
                    <Input
                      type="date"
                      value={createdAtToFilter}
                      onChange={(e) => setCreatedAtToFilter(e.target.value)}
                      className="h-9 text-sm"
                    />
                  </div>
                  
                  <div className="space-y-1.5 min-w-[140px]">
                    <label className="text-xs font-medium text-muted-foreground">
                      {t('leads.report.filters.media')}
                    </label>
                    <Select value={sourceFilter} onValueChange={setSourceFilter}>
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue placeholder={t('leads.report.filters.allMedia')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('leads.report.filters.allMedia')}</SelectItem>
                        {leadOptions.sources.map((source) => (
                          <SelectItem key={source.value} value={source.value}>
                            {source.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-1.5 min-w-[140px]">
                    <label className="text-xs font-medium text-muted-foreground">
                      {t('leads.report.filters.stage')}
                    </label>
                    <Select value={funnelStageFilter} onValueChange={setFunnelStageFilter}>
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue placeholder={t('leads.report.filters.allStages')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('leads.report.filters.allStages')}</SelectItem>
                        {leadOptions.funnelStages.map((stage) => (
                          <SelectItem key={stage.value} value={stage.value}>
                            {stage.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-1.5 min-w-[140px]">
                    <label className="text-xs font-medium text-muted-foreground">
                      {t('leads.report.filters.status')}
                    </label>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue placeholder={t('leads.report.filters.allStatuses')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('leads.report.filters.allStatuses')}</SelectItem>
                        {leadOptions.statuses.map((status) => (
                          <SelectItem key={status.value} value={status.value}>
                            {status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-1.5 min-w-[140px]">
                    <label className="text-xs font-medium text-muted-foreground">
                      {t('leads.report.filters.phone')}
                    </label>
                    <Input
                      type="text"
                      value={phoneFilter}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      placeholder={t('leads.report.filters.phonePlaceholder')}
                      className="h-9 text-sm"
                    />
                  </div>
                  
                  <div className="space-y-1.5 min-w-[140px]">
                    <label className="text-xs font-medium text-muted-foreground">
                      {t('leads.report.filters.email')}
                    </label>
                    <Input
                      type="email"
                      value={emailFilter}
                      onChange={(e) => setEmailFilter(e.target.value)}
                      placeholder={t('leads.report.filters.emailPlaceholder')}
                      className="h-9 text-sm"
                    />
                  </div>
                  
                  {hasActiveFilters && (
                    <div className="space-y-1.5">
                      <div className="h-4"></div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={clearAllFilters}
                        className="h-9 text-xs"
                      >
                        {t('leads.report.filters.clear')}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              
              {isLoading ? (
                <div className="p-4">
                  <ListPageSkeleton variant="table" rows={10} showFilters={false} />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="whitespace-nowrap">{t('leads.report.column.name')}</TableHead>
                        <TableHead className="whitespace-nowrap">{t('leads.report.column.document')}</TableHead>
                        <TableHead className="whitespace-nowrap">{t('leads.report.column.status')}</TableHead>
                        <TableHead className="whitespace-nowrap">{t('leads.report.column.source')}</TableHead>
                        <TableHead className="whitespace-nowrap">{t('leads.report.column.phone')}</TableHead>
                        <TableHead className="whitespace-nowrap">{t('leads.report.column.email')}</TableHead>
                        <TableHead className="whitespace-nowrap">{t('leads.report.column.assigned')}</TableHead>
                        <TableHead className="whitespace-nowrap">{t('leads.report.column.stage')}</TableHead>
                        <TableHead className="whitespace-nowrap">{t('leads.report.column.createdAt')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {leads.map((lead) => {
                        const fid =
                          (lead as { funnelStageId?: string }).funnelStageId ||
                          (lead as { stageId?: string }).stageId
                        const stageLabel = fid ? stageNameById.get(fid) ?? fid : '—'
                        const statusLabel =
                          leadOptions.statuses.find((s) => s.value === lead.status)?.label ?? lead.status
                        const sourceLabel =
                          leadOptions.sources.find((s) => s.value === lead.source)?.label ?? lead.source
                        return (
                          <TableRow key={lead.id}>
                            <TableCell className="font-medium whitespace-nowrap max-w-[200px] truncate">
                              {lead.name}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">{lead.document ?? '—'}</TableCell>
                            <TableCell className="whitespace-nowrap">{statusLabel}</TableCell>
                            <TableCell className="whitespace-nowrap">{sourceLabel}</TableCell>
                            <TableCell className="whitespace-nowrap">{formatLeadPrimaryPhone(lead) || '—'}</TableCell>
                            <TableCell className="whitespace-nowrap max-w-[180px] truncate">
                              {formatLeadPrimaryEmail(lead) || '—'}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {lead.assignedUser?.name ?? '—'}
                            </TableCell>
                            <TableCell className="whitespace-nowrap max-w-[140px] truncate">{stageLabel}</TableCell>
                            <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                              {lead.createdAt
                                ? new Date(lead.createdAt).toLocaleString(undefined, {
                                    dateStyle: 'short',
                                    timeStyle: 'short',
                                  })
                                : '—'}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
              {!isLoading && (
                <div className="px-4 py-3 border-t border-border text-xs text-muted-foreground flex justify-between gap-2">
                  <span>
                    {t('leads.report.showingCount', {
                      count: leads.length,
                      total: pagination.total,
                    })}
                  </span>
                  <span>{t('leads.report.exportHint')}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
